import {
  AfterViewInit,
  Component,
  computed,
  ElementRef,
  inject,
  Injector,
  model,
  OnInit,
  signal,
  Type,
  viewChild
} from '@angular/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { NgClass, NgComponentOutlet } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import {
  faBell,
  faBellSlash,
  faCheckCircle,
  faCircleExclamation,
  faEnvelope,
  faPrint,
  faSortDown,
  faSortUp,
  faSpinner,
  faStar,
  faTriangleExclamation,
  faFilter,
} from '@fortawesome/free-solid-svg-icons';
import {
  AlertType,
  DateGroupedTeacherNotifications,
  DisplayedTeacherNotification, TeacherNotification,
  TeacherNotificationResponse, TeacherUsageNotification,
  WhereToLookMap
} from '../../core/models/notification.model';
import { NotificationsService } from '../../core/services/notifications.service';
import { CATEGORY_ICONS } from 'src/app/shared/standards/icons';
import { DETAILED_NOTIFICATIONS } from '../detailed/registry';
import { DIALOG_CLOSE, DISMISS_NOTIFICATIONS } from '../../core/models/tokens';
import { RefreshButton } from '../../shared/components/refresh-button/refresh-button.component';

import introJs from 'intro.js';
import { notificationsIntroSteps } from './notifications.intro-steps';

@Component({
  selector: 'wf-notifications-overview',
  standalone: true,
  imports: [FontAwesomeModule, NgClass, FormsModule, RouterLink, NgComponentOutlet, RefreshButton],
  templateUrl: './overview.component.html',
  styleUrls: ['../../core/styles/dialog.css', './overview.component.css'],
})
export class NotificationsOverviewComponent implements OnInit, AfterViewInit {
  private readonly rootInjector = inject(Injector);
  private readonly notificationsService = inject(NotificationsService);
  private readonly activatedRoute = inject(ActivatedRoute);

  readonly notificationsIcon = CATEGORY_ICONS['notifications'];
  readonly pageHeaderIcon = faBell;
  readonly emailIcon = faEnvelope;
  readonly celebrationIcon = faStar;
  readonly needsAttentionIcon = faTriangleExclamation;
  readonly printIcon = faPrint;
  readonly sortDownIcon = faSortDown;
  readonly sortUpIcon = faSortUp;
  readonly successIcon = faCheckCircle;
  readonly errorIcon = faCircleExclamation;
  readonly loadingIcon = faSpinner;
  readonly dismissIcon = faBellSlash;
  readonly filterIcon = faFilter;

  readonly searchTerm = model('');
  readonly mode = signal<'recent' | 'history'>('recent');
  readonly notifications = signal<DateGroupedTeacherNotifications>({});
  readonly sortAscending = signal(false);
  readonly message = signal('');
  readonly messageTitle = signal('');
  readonly messageStatus = signal('');
  readonly activeNotification = signal<Type<any> | null>(null);
  readonly activeNotificationData = signal<(TeacherNotification | TeacherUsageNotification)[] | null>(null);
  readonly checkedGroups = signal<Set<string>>(new Set());
  readonly isDismissing = signal(false);
  readonly startDate = model('');
  readonly endDate = model('');

  readonly notificationCount = computed(() => {
    return Object.values(this.notifications())
      .reduce((total, notificationTypes) => total + Object.values(notificationTypes ?? {}).length, 0);
  });
  readonly selectedCount = computed(() => this.checkedGroups().size);
  readonly filteredNotifications = computed(() => {
    const term = this.searchTerm().toLowerCase().trim();
    return this.notificationsService.filterNotifications(this.notifications(), term);
  });
  readonly displayedNotifications = computed(() => {
    const displayed: DisplayedTeacherNotification[] = [];
    const filtered = this.filteredNotifications();
    const isAscending = this.sortAscending();

    for (const [date, notificationTypes] of Object.entries(filtered)) {
      for (const [notificationType, notifications] of Object.entries(notificationTypes ?? {})) {
        if (!notifications || notifications.length === 0) continue;

        // Determine our student count
        let studentCount = notifications.length;
        if ('notificationId' in notifications[0]) {
          studentCount = notifications[0].buckets.reduce((acc, bucket) => acc += bucket.count, 0);
        }

        const { category, label, route } = WhereToLookMap[notificationType as AlertType];
        displayed.push({
          type: notificationType,
          studentCount: studentCount,
          category: notifications[0].category,
          text: notifications[0].text,
          displayDate: date,
          whereToLookCategory: category,
          whereToLookLabel: label,
          whereToLookRoute: route,
        });
      }
    }

    return displayed.sort((a, b) => {
      const timeA = Date.parse(a.displayDate) || 0;
      const timeB = Date.parse(b.displayDate) || 0;

      if (timeA === timeB) {
        return a.text.localeCompare(b.text);
      }
      return isAscending ? timeA - timeB : timeB - timeA;
    });
  });

  // Dialog infrastructure
  readonly notificationInjector = Injector.create({
    providers: [
      {
        provide: DIALOG_CLOSE,
        useValue: () => this.onNotificationClosed(),
      },
      {
        provide: DISMISS_NOTIFICATIONS,
        useValue: (alertIds: number[], notificationIds: number[]) => this.dismissNotifications(alertIds, notificationIds),
      }
    ],
    parent: this.rootInjector
  });

  readonly notificationDialog = viewChild<ElementRef<HTMLDialogElement>>('notificationDialog');

  introTour: any;

  ngOnInit(): void {
    const data = this.activatedRoute.snapshot.data['notifications'];
    this.mode.set(this.activatedRoute.snapshot.data['mode'] ?? 'recent');

    if (data?.['isErred']) {
      this.messageStatus.set('error');
      this.messageTitle.set('Error Loading Notifications');
      this.message.set(data['errorMsg'] ?? `Unable to load ${this.mode()} notifications. Please try again.`);
    } else {
      this.notifications.set(data?.['data'] as DateGroupedTeacherNotifications ?? {});
    }
  }

  ngAfterViewInit() {
    this.setupIntroTour();
  }

  private filterIntroStepsByExistingElements(steps: any[]): any[] {
    return steps.filter(step => {
      if (!step.element) {
        return true;
      }

      try {
        const element = document.querySelector(step.element);
        return element !== null;
      } catch (error) {
        console.warn(`Invalid selector in intro step: ${step.element}`, error);
        return false;
      }
    });
  }

  setupIntroTour() {
    this.introTour = introJs();
    this.introTour.setOptions({
      steps: notificationsIntroSteps,
      nextLabel: 'Next →',
      prevLabel: '← Back',
      doneLabel: 'Done',
      exitOnOverlayClick: false,
      showStepNumbers: true,
      disableInteraction: false,
      scrollToElement: true,
      showBullets: true,
      showSkip: false,
    });
  }

  startTour() {
    const filteredSteps = this.filterIntroStepsByExistingElements(notificationsIntroSteps);
    this.introTour.setOptions({ steps: filteredSteps, tooltipPosition: 'bottom' });
    this.introTour.start();
  }

  toggleSort(): void {
    this.sortAscending.update((isAscending) => !isAscending);
  }

  openNotification(date: string, type: string): void {
    const filtered = this.filteredNotifications() ?? {};
    if (Object.values(filtered).length === 0) return;

    const notifs = filtered[date]?.[type as AlertType] ?? [];
    const detailedNotification = DETAILED_NOTIFICATIONS[type as AlertType];

    if (!detailedNotification) return;

    this.activeNotification.set(detailedNotification);
    this.activeNotificationData.set(notifs);
    this.notificationDialog()?.nativeElement.showModal();
  }

  onNotificationClosed(): void {
    this.notificationDialog()?.nativeElement.close();
    this.activeNotification.set(null);
    this.activeNotificationData.set(null);
  }

  toggleGroup(e: Event, groupKey: string): void {
    const checked = (e.target as HTMLInputElement).checked;
    this.checkedGroups.update(set => {
      const checkedSet = new Set(set);
      if (checked) checkedSet.add(groupKey);
      else checkedSet.delete(groupKey);
      return checkedSet;
    });
  }

  dismissSelected(): void {
    if (!this.checkedGroups().size) return;
    this.isDismissing.set(true);

    const activeGroups = this.checkedGroups();
    const filtered = this.filteredNotifications();
    const alertIds: number[] = [];
    const notificationIds: number[] = [];

    for (const [date, notificationTypes] of Object.entries(filtered)) {
      for (const [notificationType, notifications] of Object.entries(notificationTypes ?? {})) {
        const groupKey = `${date}_${notificationType}`;
        if (activeGroups.has(groupKey) && notifications) {
          for (const notif of notifications) {
            if ('alertID' in notif) {
              alertIds.push(notif.alertID);
            } else if ('notificationId' in notif) {
              notificationIds.push(notif.notificationId);
            }
          }
        }
      }
    }

    this.dismissNotifications(alertIds, notificationIds);
  }

  dismissNotifications(alertIds: number[], notificationIds: number[]) {
    this.notificationsService.dismissNotifications(alertIds, notificationIds).subscribe({
      next: () => {
        if (this.activeNotification() !== null) {
          this.onNotificationClosed();
        }

        this.isDismissing.set(false);
        this.checkedGroups.set(new Set());
        this.messageStatus.set('success');
        this.messageTitle.set('Notifications Dismissed');
        this.message.set('Selected notifications have been moved to history.');
        this.reloadRecentNotifications();
      },
      error: () => {
        this.isDismissing.set(false);
        this.messageStatus.set('error');
        this.messageTitle.set('Dismiss Failed');
        this.message.set('Unable to dismiss notifications. Please try again.');
      }
    });
  }

  reloadRecentNotifications(): void {
    this.notificationsService.getRecentNotifications().subscribe({
      next: (data: TeacherNotificationResponse) => {
        if (data.isErred) {
          this.messageStatus.set('error');
          this.messageTitle.set('Error Loading Notifications');
          this.message.set(data.errorMsg ?? 'Unable to load recent notifications. Please try again.');
        } else {
          this.notifications.set(data.data ?? {});
        }
      },
    });
  }

  applyDateFilter(): void {
    if (this.mode() !== 'history') return;

    this.messageStatus.set('');
    this.message.set('');

    // Fallback to undefined if fields are empty
    const start = this.startDate() || undefined;
    const end = this.endDate() || undefined;

    this.notificationsService.getHistoryNotifications(start, end).subscribe({
      next: (data: TeacherNotificationResponse) => {
        if (data.isErred) {
          this.messageStatus.set('error');
          this.messageTitle.set('Error Loading History');
          this.message.set(data.errorMsg ?? 'Unable to load historical notifications.');
        } else {
          this.notifications.set(data.data ?? {});
        }
      },
      error: () => {
        this.messageStatus.set('error');
        this.messageTitle.set('Network Error');
        this.message.set('Something went wrong. Please check your connection and try again.');
      }
    });
  }
}
